import { useEffect, useMemo, useState } from 'react';

import type { Order } from '#domain/Order.ts';
import { anchorTourEndpointsAtEdges, getStopsFromOrders, type TourStop } from '#domain/TourStop.ts';

const getStopKey = (stop: TourStop) => `${stop.orderId}:${stop.stopId ?? stop.id}`;

export const useHandleTourStops = (isOpen: boolean, orders: Order[], initialStops?: TourStop[]) => {
    const [sortedTourStops, setSortedTourStops] = useState<TourStop[]>([]);

    useEffect(() => {
        if (isOpen) {
            setSortedTourStops(currentStops => {
                const nextStops = initialStops ?? getStopsFromOrders(orders);
                const nextStopById = new Map(nextStops.map(stop => [getStopKey(stop), stop]));
                const currentStopIds = new Set(currentStops.map(getStopKey));
                const retainedStops = currentStops.flatMap(stop => {
                    if (stop.chargingDraftId) {
                        return [stop];
                    }
                    const nextStop = nextStopById.get(getStopKey(stop));
                    if (!nextStop) {
                        return [];
                    }
                    // Group indices follow the selected orders; all other fields belong to the draft.
                    return [stop.group === nextStop.group ? stop : { ...stop, group: nextStop.group }];
                });
                const addedStops = nextStops.filter(stop => !currentStopIds.has(getStopKey(stop)));
                const reconciledStops = anchorTourEndpointsAtEdges([...retainedStops, ...addedStops]);
                return reconciledStops.length === currentStops.length &&
                    reconciledStops.every((stop, index) => stop === currentStops[index])
                    ? currentStops
                    : reconciledStops;
            });
        } else {
            setSortedTourStops([]);
        }
    }, [initialStops, isOpen, orders]);

    const misplacedStopIds = useMemo(() => findMisplacedStopIds(sortedTourStops), [sortedTourStops]);

    return {
        sortableTourStops: sortedTourStops,
        setSortableTourStops: setSortedTourStops,
        isMisplaced: misplacedStopIds.length > 0,
        misplacedStopIds,
    };
};

export const findMisplacedStopIds = (stops: TourStop[]): string[] => {
    const misplacedIds = new Set<string>();
    const starts = stops.filter(stop => stop.serviceOrderCategory === 'tour-start');
    const destinations = stops.filter(stop => stop.serviceOrderCategory === 'tour-end');
    if (starts.length > 1) {
        for (const stop of starts) {
            misplacedIds.add(stop.id);
        }
    }
    if (destinations.length > 1) {
        for (const stop of destinations) {
            misplacedIds.add(stop.id);
        }
    }
    stops.forEach((stop, index) => {
        if (stop.serviceOrderCategory === 'tour-start' && index !== 0) {
            misplacedIds.add(stop.id);
        }
        if (stop.serviceOrderCategory === 'tour-end' && index !== stops.length - 1) {
            misplacedIds.add(stop.id);
        }
        if (stop.type === 'unload') {
            const loadIndex = stops.findIndex(other => other.group === stop.group && other.type === 'load');
            if (loadIndex !== -1 && index < loadIndex) {
                misplacedIds.add(stop.id);
            }
        }
    });
    return [...misplacedIds];
};
